export interface Currency {
  code: string;
  name: string;
  symbol: string;
  exchangeRate: number;
  isDefault: boolean;
  isActive: boolean;
}

export interface TaxRateInfo {
  id: string;
  name: string;
  rate: number;
  type: 'INCLUSIVE' | 'EXCLUSIVE';
  isActive?: boolean;
}

export interface TaxAssignment {
  taxRateId: string;
  taxRate: TaxRateInfo;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  imageUrl: string | null;
  parentId: string | null;
  sortOrder: number;
  skipPaymentMethodTax?: boolean;
  _count?: { products: number };
  children?: Category[];
  taxes?: TaxAssignment[];
}

export interface Product {
  id: string;
  name: string;
  sku: string;
  barcode: string | null;
  salePrice: number;
  costPrice: number;
  taxRate: number;
  taxInclusive: boolean;
  taxOverride: boolean;
  stockQuantity: number;
  trackStock: boolean;
  serialTracking?: boolean; // For IMEI/serial number tracking
  unit: string;
  uom?: string;
  hsCode?: string | null;
  fbrValidated?: boolean;
  fbrValidationError?: string | null;
  imageUrl: string | null;
  expiryTracking?: boolean;
  hasExpiredBatches?: boolean;
  skipPaymentMethodTax?: boolean;
  category: { id: string; name: string; imageUrl: string | null; skipPaymentMethodTax?: boolean; taxes?: TaxAssignment[] } | null;
  variants: ProductVariant[];
  taxes?: TaxAssignment[];
}

export interface ProductVariant {
  id: string;
  name: string;
  sku: string;
  priceAdjustment: number;
  stockQuantity: number;
}

export interface TaxBreakdownItem {
  name: string;
  rate: number;
  type: 'INCLUSIVE' | 'EXCLUSIVE';
  amount: number;
}

export interface CartItem {
  id: string; // unique cart line id
  product: Product;
  variant: ProductVariant | null;
  quantity: number;
  baseSalePrice: number; // original price before any tier adjustment — used for repricing when customer changes
  unitPrice: number;
  discountType: 'PERCENTAGE' | 'FIXED' | null;
  discountValue: number;
  discountAmount: number;
  /** POS-I119: why the line discount was given, and the manager who approved it (PIN approval). */
  discountReason?: string;
  discountApprovedById?: string;
  taxAmount: number;
  taxBreakdown: TaxBreakdownItem[];
  lineTotal: number;
  serialNumberId?: string | null; // For tracked products, the selected serial number ID
}

export interface Customer {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  ntn?: string | null;
  cnic?: string | null;
  isOnAtl?: boolean;
  atlVerifiedAt?: string | null;
  priceTierId?: string | null;
  priceTier?: { id: string; name: string; adjustmentPercent?: number | null } | null;
}

export interface SerialNumber {
  id: string;
  productId: string;
  serialNo: string;
  status: 'AVAILABLE' | 'SOLD' | 'RETURNED' | 'DAMAGED';
  notes?: string | null;
  soldAt?: string | null;
  createdAt?: string;
}

export type PaymentMethod = 'CASH' | 'CARD' | 'BANK_TRANSFER' | 'MOBILE_WALLET' | 'CREDIT' | 'CHEQUE' | 'GIFT_CARD' | 'QR_CODE' | 'ACCOUNT';

export interface PaymentEntry {
  method: PaymentMethod;
  amount: number;
  reference?: string;
}

export interface DailySummary {
  date: string;
  totalSales: number;
  totalRevenue: number;
  totalTax: number;
  totalDiscount: number;
}

// A money value may arrive from the API as a number or a Decimal-encoded string.
export type Money = number | string;

export interface SaleItem {
  id?: string;
  productId?: string;
  variantId?: string | null;
  serialNumberId?: string | null;
  /** T-484: present on receipt reads for serial-tracked lines. */
  serialNumber?: { serialNo: string; imei1?: string | null; imei2?: string | null; warrantyExpiry?: string | null } | null;
  productName?: string;
  productSku?: string;
  sku?: string;
  uom?: string;
  quantity?: number;
  unitPrice?: Money;
  costPrice?: Money;
  price?: Money;
  discountType?: string;
  discountValue?: Money;
  discountAmount?: Money;
  /** POS-I119: audit trail of a line discount. */
  discountReason?: string | null;
  promotionDiscountAmount?: Money;
  furtherTaxAmount?: Money;
  taxRate?: Money;
  taxAmount?: Money;
  taxBreakdown?: unknown;
  totalAmount?: Money;
  lineTotal?: Money;
  name?: string;
  product?: Product | null;
  createdAt?: string;
}

export interface SalePayment {
  id?: string;
  method?: string;
  amount?: Money;
  reference?: string | null;
  bankAccountId?: string | null;
  shiftId?: string | null;
  createdAt?: string;
}

export interface ReceiptSalePromotion {
  id: string;
  discountAmount: Money;
  promotion: { name: string };
}

/** POS-I220: which sections the receipt prints (Settings > Receipt Customization). */
export interface ReceiptLayout {
  showLogo: boolean;
  showTagline: boolean;
  showAddress: boolean;
  showPhone: boolean;
  showNtn: boolean;
  showCashier: boolean;
  showCustomer: boolean;
  showTaxBreakdown: boolean;
  showLoyalty: boolean;
  showFbrQr: boolean;
  headerNote: string;
  returnPolicy: string;
}

export const RECEIPT_LAYOUT_DEFAULTS: ReceiptLayout = {
  showLogo: true,
  showTagline: true,
  showAddress: true,
  showPhone: true,
  showNtn: true,
  showCashier: true,
  showCustomer: true,
  showTaxBreakdown: true,
  showLoyalty: true,
  showFbrQr: true,
  headerNote: '',
  returnPolicy: '',
};

export function resolveReceiptLayout(raw?: Partial<ReceiptLayout> | null): ReceiptLayout {
  return { ...RECEIPT_LAYOUT_DEFAULTS, ...(raw ?? {}) };
}

export interface Receipt {
  id: string;
  invoiceNumber: string;
  createdAt: string;
  subtotal: Money;
  discountAmount: Money;
  /** POS-I119: sum of the line (bargaining) discounts; the line prices are already net of it. */
  lineDiscountAmount?: Money | null;
  taxAmount: Money;
  totalAmount: Money;
  paidAmount: Money;
  changeAmount: Money;
  tipAmount?: Money;
  loyaltyDiscountAmount?: Money | null;
  currencyCode?: string | null;
  exchangeRate?: Money | null;
  foreignAmount?: Money | null;
  /** POS-I360 (SR-69): the default currency and its rate when the sale was made, any payment method; null on older sales. */
  receiptCurrencyCode?: string | null;
  receiptExchangeRate?: Money | null;
  fbrInvoiceId?: string | null;
  customer?: { name?: string; phone?: string; ntn?: string | null; cnic?: string | null; isOnAtl?: boolean } | null;
  /** ATL status of the buyer at the time of sale (POS-I55). */
  buyerIsOnAtl?: boolean;
  /** `fbrLive` (POS-I114): FBR integration enabled and LIVE for the shop; only then may a receipt show an FBR number/QR. */
  tenant?: {
    name?: string;
    fbrLive?: boolean;
    fbrNtn?: string | null;
    /** POS-I229: the shop block is the one frozen on the sale (receiptFooter, logoUrl, tagline, layout). */
    receiptFooter?: string | null;
    logoUrl?: string | null;
    tagline?: string | null;
    /** POS-I220: which sections print; absent = all (older cached responses). */
    layout?: ReceiptLayout | null;
    /** True when the sale carries its own frozen copy; snapshotBackfilled = copy made at migration time. */
    snapshot?: boolean;
    snapshotTakenAt?: string | null;
    snapshotBackfilled?: boolean;
    timezone?: string | null;
  } | null;
  /** POS-I220: name of the cashier who made the sale. */
  cashierName?: string | null;
  location?: { name?: string; address?: string; city?: string; phone?: string } | null;
  items: SaleItem[];
  payments: SalePayment[];
  salePromotions?: ReceiptSalePromotion[];
  /** POS-I101: points earned / redeemed on this sale and the balance right after it. */
  loyalty?: { pointsEarned: number; pointsRedeemed: number; balanceAfter: number } | null;
}

export interface Sale {
  id: string;
  invoiceNumber: string;
  status: string;
  subtotal: number;
  discountAmount: number;
  lineDiscountAmount?: number;
  taxAmount: number;
  totalAmount: number;
  paidAmount: number;
  changeAmount: number;
  createdAt: string;
  customer: Customer | null;
  items: SaleItem[];
  payments: SalePayment[];
}
